// @flow
/** @jsxRuntime classic */
import "./polyfills";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import ReactDOM from "react-dom";
import { Router } from "react-router-dom";
import GoogleTagManager from "react-gtm-module";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";

import AuthenticationProvider from "./Commons/Modules/auth";
import { UserProvider } from "./Commons/Modules/auth/misc/UserContext";
import { browserHistory } from "./Commons/Modules/routing";
import Root from "./Root";

import "./Commons/Assets/fonts/remindme-categories/css/remindme-categories.css";
import "./Commons/Assets/fonts/remindme-icon/css/remindme-icons.css";
import "./styles/global.scss";
import { Environment } from "./Commons/Utilities/index";
import { scrollToPosition } from "Commons/Utilities/scrollTo";

import "react-toastify/dist/ReactToastify.css";

import store from "./redux/configureStore";
import ErrorBoundary from "./Commons/Components/ErrorBoundary/ErrorBoundary";
import * as serviceWorker from "./serviceWorker";

const GOOGLE_TAG_MANAGER_CONTAINER_ID = Environment.get(Environment.GOOGLE_TAG_MANAGER_CONTAINER_ID);

if (
  typeof GOOGLE_TAG_MANAGER_CONTAINER_ID !== "undefined" &&
  GOOGLE_TAG_MANAGER_CONTAINER_ID !== null &&
  GOOGLE_TAG_MANAGER_CONTAINER_ID !== ""
) {
  GOOGLE_TAG_MANAGER_CONTAINER_ID.split(",").forEach(function (gtm_container) {
    if (typeof gtm_container === "string" && gtm_container.trim() !== "") {
      GoogleTagManager.initialize({ gtmId: gtm_container });
    }
  });
}

const SENTRY_DSN = Environment.get(Environment.SENTRY_DSN);

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: Environment.get(Environment.STAGE_NAME),
    release: Environment.get(Environment.SENTRY_RELEASE),
    maxValueLength: 100000,
    ignoreErrors: [
      // Random plugins/extensions
      "top.GLOBALS",
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error. html
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.epicplay.com",
      "Can't find variable: ZiteReader",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "http://loading.retry.widdit.com/",
      "atomicFindClose",
      // Facebook borked
      "fb_xd_fragment",
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      "conduitPage",
    ],
    blacklistUrls: [
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });
}

/**
 * Created a folder structure according to the discussions earlier.
 *
 * Each feature is master of it's own fate -> has it"s own state, state management, API -> plug and play
 *
 * Each App is wrapped by ConfiguredApp, which takes care of dynamically loading the feature components,
 * based on the configuration API.
 *
 * We are (finally!!!!) using new React features:
 * - Context API for ConfigurationProvider and ConfigurationConsumer in ConfiguredApp
 * - React.Fragment to wrap Elements which otherwise have to be rendered as an array with keys
 * - React.StrictMode which gives us hints of the (soon to be) deprecated features we are using
 */

const domRoot = document.getElementById("root");

if (domRoot == null) {
  throw new Error("No DOM element with id 'root found'");
}

ReactDOM.render(
  <Provider store={store}>
    <ErrorBoundary>
      <AuthenticationProvider>
        {({ userId }) => (
          <UserProvider userId={userId}>
            <Router
              onUpdate={() => {
                scrollToPosition(0, -200);
              }}
              history={browserHistory}
            >
              <Root />
            </Router>
          </UserProvider>
        )}
      </AuthenticationProvider>
      <ToastContainer />
    </ErrorBoundary>
  </Provider>,
  domRoot
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
